// Home screen -- consolidated Business Overview across every site. Reuses
// the same company-wide data (state.sites, state.dashboardTransactions)
// already loaded by sites.js/projectHealth.js, sliced client-side by the
// date filter below. No new Supabase query. Never gated by selectedSiteId --
// Home never requires a site to be selected.

import { getState, setState } from '../state/store.js';
import { money, formatDate, todayISO } from './format.js';
import { computeTotals, expenseCategoryBreakdown } from '../pnl/pnl.js';
import { monthlyBreakdown, monthLabel } from '../reports/reports.js';
import { healthStatus, loadProjectHealth, loadDashboardTransactions } from '../dashboard/projectHealth.js';
import { loadSites } from '../sites/sites.js';
import {
  HOME_DATE_FILTERS, homeDateRange, filterForRange, pctDelta,
  dailyBreakdown, dayLabel, activeProjectsList, topClientsByIncome
} from '../dashboard/homeDashboard.js';
import { categoryDonutChart } from './reportsView.js';
import { openFormSheet, closeFormSheet } from './modals.js';
import { logout } from '../auth/auth.js';

const STATUS_LABEL = { active: 'Ongoing', paused: 'On Hold', completed: 'Completed' };
const STATUS_PILL = { active: 'green', paused: 'amber', completed: 'blue' };

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (m) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  }[m]));
}

function initials(nameOrEmail) {
  const base = (nameOrEmail || '').trim();
  if (!base) return '?';
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function deltaHtml(curr, prev) {
  const pct = pctDelta(curr, prev);
  if (pct === null) return '';
  const sign = pct >= 0 ? '+' : '';
  const cls = pct >= 0 ? 'green' : 'red';
  return `<small class="kpiDelta ${cls}">${sign}${pct.toFixed(0)}% vs last period</small>`;
}

function txRowHtml(t, siteName) {
  const isIncome = t.type === 'income';
  return `
    <div class="txRow">
      <div class="txMain">
        <b>${escapeHtml(t.description || t.category)}</b>
        <small>${siteName ? `<span class="txSiteTag">${escapeHtml(siteName)}</span> · ` : ''}${formatDate(t.entry_date)} · ${escapeHtml(t.category)}</small>
      </div>
      <div class="amount ${isIncome ? 'green' : 'red'}">${isIncome ? '+' : '−'} ${money(t.amount)}</div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Header: title/subtitle, date filter, account/profile button. Rendered
// inside the EXISTING shared top bar (src/ui/shell.js) for the Home screen
// only -- not a second header block -- so Home gets one unified header row,
// same as every other screen. No notification bell -- this app has no
// notifications feature/table to back one, and a fabricated badge count
// would violate "do not invent data."
// ---------------------------------------------------------------------------
export function homeTopBarTitleHtml() {
  return `
    <div class="homeTitleBlock">
      <h1>Home</h1>
      <div class="homeSubtitle">Business Overview (All Clients)</div>
    </div>
  `;
}

export function homeTopBarControlsHtml(state) {
  const options = HOME_DATE_FILTERS.map((f) =>
    `<option value="${f.key}"${state.homeDateFilter === f.key ? ' selected' : ''}>${escapeHtml(f.label)}</option>`
  ).join('');
  const name = state.profile?.full_name || state.session?.user?.email || '';
  return `
    <select id="homeDateFilterSelect" class="homeDateSelect" aria-label="Date range">${options}</select>
    <button type="button" class="avatarBtn" id="homeAvatarBtn" aria-label="Account">${escapeHtml(initials(name))}</button>
  `;
}

function openAccountSheet() {
  const state = getState();
  const body = document.createElement('div');
  body.className = 'accountSheetBody';
  body.innerHTML = `
    <div class="accountName">${escapeHtml(state.profile?.full_name || state.session?.user?.email || '')}</div>
    <div class="accountRole">${escapeHtml(state.profile?.role || '')}</div>
    <button type="button" class="wideBtn" id="accountLogoutBtn">Log out</button>
  `;
  body.querySelector('#accountLogoutBtn').addEventListener('click', () => {
    closeFormSheet();
    logout();
  });
  openFormSheet('Account', body);
}

// ---------------------------------------------------------------------------
// KPI cards.
// ---------------------------------------------------------------------------
function newSitesThisMonthCount(sites) {
  const t = todayISO();
  const first = `${t.slice(0, 7)}-01`;
  return sites.filter((s) => {
    const createdDate = (s.created_at || '').slice(0, 10);
    return createdDate && createdDate >= first && createdDate <= t;
  }).length;
}

function kpiGridHtml(state, totals, prevTotals) {
  const clientSet = new Set(state.sites.map((s) => (s.client_name || '').trim()).filter(Boolean));
  const activeClientCount = clientSet.size || state.sites.length;
  const newThisMonth = newSitesThisMonthCount(state.sites);

  return `
    <div class="homeKpiGrid">
      <div class="kpiCard homeKpi income">
        <div class="kpiIconCircle income">↗</div>
        <small>Total Income</small>
        <b>${money(totals.income)}</b>
        ${deltaHtml(totals.income, prevTotals?.income)}
      </div>
      <div class="kpiCard homeKpi expense">
        <div class="kpiIconCircle expense">↘</div>
        <small>Total Expense</small>
        <b>${money(totals.expense)}</b>
        ${deltaHtml(totals.expense, prevTotals?.expense)}
      </div>
      <div class="kpiCard homeKpi profit">
        <div class="kpiIconCircle profit">📶</div>
        <small>Net Profit</small>
        <b class="${totals.profit >= 0 ? 'green' : 'red'}">${money(totals.profit)}</b>
        ${deltaHtml(totals.profit, prevTotals?.profit)}
      </div>
      <div class="kpiCard homeKpi clients">
        <div class="kpiIconCircle clients">👥</div>
        <small>Active Clients</small>
        <b>${activeClientCount}</b>
        ${newThisMonth > 0 ? `<small class="kpiDelta green">+${newThisMonth} new this month</small>` : ''}
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Charts.
// ---------------------------------------------------------------------------
// Thins daily labels to every 5th day (plus day 1) so they read like the
// reference's "1 Jun / 5 Jun / 10 Jun" axis instead of a dense run of every
// single day -- the full date is always still in the bar's hover title.
function dailyChartRows(tx, range) {
  return dailyBreakdown(tx, range.from, range.to).map((r) => {
    const dayNum = Number(r.day.slice(8, 10));
    return {
      label: dayLabel(r.day),
      showLabel: dayNum === 1 || dayNum % 5 === 0,
      income: r.income,
      expense: r.expense
    };
  });
}

function incomeExpenseChartHtml(state, tx, range) {
  const useDaily = state.homeDateFilter === 'today' || state.homeDateFilter === 'month' || state.homeDateFilter === 'lastMonth';
  const rows = useDaily
    ? dailyChartRows(tx, range)
    : monthlyBreakdown(tx).map((r) => ({ label: monthLabel(r.month), showLabel: true, income: r.income, expense: r.expense }));

  if (!rows.length) {
    return `<div class="chartCard"><h3>Income vs Expense</h3><div class="empty">No data for this period.</div></div>`;
  }
  // Zero-data periods are common (e.g. "This Month" before anything's been
  // entered yet) -- show the same deliberate empty state the rest of Home
  // uses instead of a row of barely-visible 2%-height slivers.
  const hasActivity = rows.some((r) => r.income > 0 || r.expense > 0);
  if (!hasActivity) {
    return `<div class="chartCard"><h3>Income vs Expense</h3><div class="empty">No data for this period.</div></div>`;
  }

  const max = Math.max(1, ...rows.map((r) => Math.max(r.income, r.expense)));
  return `
    <div class="chartCard">
      <h3>Income vs Expense</h3>
      <div class="monthlyTrendScroll">
        <div class="monthlyTrendRow">
          ${rows.map((r) => `
            <div class="monthlyTrendCol">
              <div class="monthlyTrendBars" title="${escapeHtml(r.label)}: Income ${money(r.income)}, Expense ${money(r.expense)}">
                <div class="monthlyTrendBar income" style="height:${Math.max(2, (r.income / max) * 100)}%"></div>
                <div class="monthlyTrendBar expense" style="height:${Math.max(2, (r.expense / max) * 100)}%"></div>
              </div>
              <div class="monthlyTrendLabel">${r.showLabel ? escapeHtml(r.label) : ''}</div>
            </div>
          `).join('')}
        </div>
      </div>
      <div class="chartLegend">
        <span><i class="legendDot income"></i> Income</span>
        <span><i class="legendDot expense"></i> Expense</span>
      </div>
    </div>
  `;
}

function expenseCategoryChartHtml(tx) {
  const rows = expenseCategoryBreakdown(tx);
  return `
    <div class="chartCard">
      <h3>Expense by Category</h3>
      ${rows.length ? categoryDonutChart(rows, 'Total Expense') : '<div class="empty">No expenses for this period.</div>'}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Tables: Top Clients by Income, Active Projects/Clients, Recent Transactions.
// ---------------------------------------------------------------------------
function topClientsTableHtml(tx, sites) {
  const rows = topClientsByIncome(tx, sites);
  const top = rows.slice(0, 4);
  const rest = rows.slice(4);
  const restAgg = rest.length
    ? rest.reduce((acc, r) => ({ income: acc.income + r.income, expense: acc.expense + r.expense, profit: acc.profit + r.profit }), { income: 0, expense: 0, profit: 0 })
    : null;

  const bodyRows = top.map((r, i) => `
    <tr class="clickRow" data-client="${escapeHtml(r.client)}">
      <td>${i + 1}</td>
      <td>${escapeHtml(r.client)}</td>
      <td class="right green">${money(r.income)}</td>
      <td class="right red">${money(r.expense)}</td>
      <td class="right ${r.profit >= 0 ? 'green' : 'red'}">${money(r.profit)}</td>
    </tr>
  `).join('');
  const otherRow = restAgg ? `
    <tr>
      <td>${top.length + 1}</td>
      <td>Other Clients (${rest.length})</td>
      <td class="right green">${money(restAgg.income)}</td>
      <td class="right red">${money(restAgg.expense)}</td>
      <td class="right ${restAgg.profit >= 0 ? 'green' : 'red'}">${money(restAgg.profit)}</td>
    </tr>
  ` : '';

  return `
    <div class="chartCard homeTableCard">
      <div class="homeTableHead"><h3>Top Clients by Income</h3>${rows.length > 4 ? `<button type="button" class="viewAllTxBtn homeViewAllBtn" data-view-all="clients">View All</button>` : ''}</div>
      ${rows.length ? `
        <div class="reportTableWrap"><table class="reportTable">
          <thead><tr><th>#</th><th>Client</th><th class="right">Income</th><th class="right">Expense</th><th class="right">Net Profit</th></tr></thead>
          <tbody>${bodyRows}${otherRow}</tbody>
        </table></div>
      ` : '<div class="empty">No client activity for this period.</div>'}
    </div>
  `;
}

function activeProjectsTableHtml(tx, sites) {
  const rows = activeProjectsList(tx, sites);
  const top = rows.slice(0, 5);

  const bodyRows = top.map((r) => `
    <tr class="clickRow" data-site-id="${r.id}">
      <td><b>${escapeHtml(r.name)}</b>${r.client_name ? `<br><small class="muted">${escapeHtml(r.client_name)}</small>` : ''}</td>
      <td><span class="statusPill ${STATUS_PILL[r.status] || 'gray'}">${escapeHtml(STATUS_LABEL[r.status] || r.status || 'Unknown')}</span></td>
      <td class="right green">${money(r.income)}</td>
      <td class="right red">${money(r.expense)}</td>
    </tr>
  `).join('');

  return `
    <div class="chartCard homeTableCard">
      <div class="homeTableHead"><h3>Active Projects / Clients</h3>${rows.length > 5 ? `<button type="button" class="viewAllTxBtn homeViewAllBtn" data-view-all="projects">View All</button>` : ''}</div>
      ${rows.length ? `
        <div class="reportTableWrap"><table class="reportTable">
          <thead><tr><th>Client / Site</th><th>Status</th><th class="right">Income</th><th class="right">Expense</th></tr></thead>
          <tbody>${bodyRows}</tbody>
        </table></div>
      ` : '<div class="empty">No sites yet.</div>'}
    </div>
  `;
}

function recentTransactionsTableHtml(state, tx) {
  const siteNameById = {};
  for (const s of state.sites) siteNameById[s.id] = s.name;
  const recent = tx.slice(0, 15); // tx is already date-filtered and ordered (entry_date desc, created_at desc) from loadDashboardTransactions()

  const rows = recent.map((t) => `
    <tr>
      <td>${formatDate(t.entry_date)}</td>
      <td>${escapeHtml(t.description || t.category)}</td>
      <td>${escapeHtml(siteNameById[t.site_id] || 'Unknown site')}</td>
      <td>${escapeHtml(t.category)}</td>
      <td>${t.type === 'income' ? 'Income' : 'Expense'}</td>
      <td class="right ${t.type === 'income' ? 'green' : 'red'}">${t.type === 'income' ? '+' : '−'} ${money(t.amount)}</td>
    </tr>
  `).join('');

  return `
    <div class="chartCard homeTableCard">
      <div class="homeTableHead"><h3>Recent Transactions (All Clients)</h3>${tx.length > 15 ? `<button type="button" class="viewAllTxBtn homeViewAllBtn" data-view-all="transactions">View All</button>` : ''}</div>
      ${recent.length ? `
        <div class="reportTableWrap"><table class="reportTable">
          <thead><tr><th>Date</th><th>Description</th><th>Client / Site</th><th>Category</th><th>Type</th><th class="right">Amount</th></tr></thead>
          <tbody>${rows}</tbody>
        </table></div>
      ` : '<div class="empty">No transactions yet.</div>'}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Project Health -- existing quotation-vs-spend feature, preserved as-is
// (just relocated out of shell.js into this file), appended below the new
// reference-driven sections rather than removed.
// ---------------------------------------------------------------------------
function projectHealthCard(h) {
  const status = healthStatus(h);
  const barWidth = status.noQuotation ? 0 : Math.max(0, Math.min(h.pct, 100));
  const pctLabel = status.noQuotation
    ? (h.expense > 0 ? 'Spending recorded — no quotation set' : 'No quotation set')
    : `${h.pct.toFixed(0)}% Used`;
  return `
    <div class="healthCard">
      <div class="healthCardHead">
        <b>${escapeHtml(h.name)}</b>
        <span class="statusPill ${status.pill}">${status.emoji} ${status.label}</span>
      </div>
      <div class="healthStatsRow">
        <div><small>Quotation</small><b>${money(h.quotation)}</b></div>
        <div><small>Spent</small><b class="red">${money(h.expense)}</b></div>
        <div><small>Remaining</small><b class="${h.remaining >= 0 ? 'green' : 'red'}">${money(h.remaining)}</b></div>
      </div>
      <div class="healthBarTrack${status.noQuotation ? ' noQuotation' : ''}"><div class="healthBarFill ${status.pill}" style="width:${barWidth}%"></div></div>
      <div class="healthPctLabel">${pctLabel}</div>
    </div>
  `;
}

function projectHealthSection(state) {
  if (state.projectHealthError) {
    return `
      <div class="sectionHead"><h2>Project Health (Quotation vs Spend)</h2></div>
      <div class="errorBanner"><span>Couldn't load project health.</span><button type="button" class="retryBtn" data-retry="health">Retry</button></div>
    `;
  }
  return `
    <div class="sectionHead"><h2>Project Health (Quotation vs Spend)</h2></div>
    <div class="healthGrid">
      ${state.projectHealthLoading ? '<div class="empty">Loading…</div>' : ''}
      ${!state.projectHealthLoading && !state.projectHealth.length ? '<div class="empty">No sites yet.</div>' : ''}
      ${!state.projectHealthLoading ? state.projectHealth.map(projectHealthCard).join('') : ''}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// "View All" / row drill-down sheets -- reuse the existing form-sheet overlay
// (src/ui/modals.js) instead of new routes/nav items.
// ---------------------------------------------------------------------------
function openViewAllSheet(kind, state) {
  const range = homeDateRange(state.homeDateFilter);
  const tx = filterForRange(state.dashboardTransactions, range.from, range.to);
  const body = document.createElement('div');

  if (kind === 'clients') {
    const rows = topClientsByIncome(tx, state.sites);
    body.className = 'reportTableWrap';
    body.innerHTML = `
      <table class="reportTable">
        <thead><tr><th>#</th><th>Client</th><th class="right">Income</th><th class="right">Expense</th><th class="right">Net Profit</th></tr></thead>
        <tbody>${rows.length ? rows.map((r, i) => `
          <tr><td>${i + 1}</td><td>${escapeHtml(r.client)}</td><td class="right green">${money(r.income)}</td><td class="right red">${money(r.expense)}</td><td class="right ${r.profit >= 0 ? 'green' : 'red'}">${money(r.profit)}</td></tr>
        `).join('') : '<tr><td colspan="5" class="empty">No client activity for this period.</td></tr>'}</tbody>
      </table>
    `;
    openFormSheet('All Clients', body);
    return;
  }

  if (kind === 'projects') {
    const rows = activeProjectsList(tx, state.sites);
    body.className = 'reportTableWrap';
    body.innerHTML = `
      <table class="reportTable">
        <thead><tr><th>Client / Site</th><th>Status</th><th class="right">Income</th><th class="right">Expense</th></tr></thead>
        <tbody>${rows.length ? rows.map((r) => `
          <tr><td><b>${escapeHtml(r.name)}</b></td><td><span class="statusPill ${STATUS_PILL[r.status] || 'gray'}">${escapeHtml(STATUS_LABEL[r.status] || r.status || 'Unknown')}</span></td><td class="right green">${money(r.income)}</td><td class="right red">${money(r.expense)}</td></tr>
        `).join('') : '<tr><td colspan="4" class="empty">No sites yet.</td></tr>'}</tbody>
      </table>
    `;
    openFormSheet('All Projects / Clients', body);
    return;
  }

  // transactions
  const siteNameById = {};
  for (const s of state.sites) siteNameById[s.id] = s.name;
  body.className = 'txList';
  body.innerHTML = tx.length ? tx.map((t) => txRowHtml(t, siteNameById[t.site_id] || 'Unknown site')).join('') : '<div class="empty">No transactions in this period.</div>';
  openFormSheet('All Transactions (All Clients)', body);
}

function openClientDrilldownSheet(clientName, state) {
  const range = homeDateRange(state.homeDateFilter);
  const tx = filterForRange(state.dashboardTransactions, range.from, range.to);
  const siteIds = new Set(
    state.sites.filter((s) => ((s.client_name || '').trim() || 'Unspecified Client') === clientName).map((s) => s.id)
  );
  const siteNameById = {};
  for (const s of state.sites) siteNameById[s.id] = s.name;
  const rows = tx.filter((t) => siteIds.has(t.site_id));

  const body = document.createElement('div');
  body.className = 'txList';
  body.innerHTML = rows.length ? rows.map((t) => txRowHtml(t, siteNameById[t.site_id])).join('') : '<div class="empty">No transactions in this period.</div>';
  openFormSheet(`${clientName} — Transactions`, body);
}

function openSiteDrilldownSheet(siteId, state) {
  const range = homeDateRange(state.homeDateFilter);
  const tx = filterForRange(state.dashboardTransactions, range.from, range.to).filter((t) => t.site_id === siteId);
  const site = state.sites.find((s) => s.id === siteId);

  const body = document.createElement('div');
  body.className = 'txList';
  body.innerHTML = tx.length ? tx.map((t) => txRowHtml(t)).join('') : '<div class="empty">No transactions in this period.</div>';
  openFormSheet(`${site ? site.name : 'Site'} — Transactions`, body);
}

// ---------------------------------------------------------------------------
// Top-level render + wiring.
// ---------------------------------------------------------------------------
export function homeSection(state) {
  if (!state.sitesLoaded || state.dashboardTransactionsLoading) {
    return `<div class="empty">Loading dashboard…</div>`;
  }
  if (state.sitesError || state.dashboardTransactionsError) {
    return `
      <div class="errorBanner"><span>Couldn't load dashboard data.</span><button type="button" class="retryBtn" data-retry="home">Retry</button></div>
    `;
  }

  const range = homeDateRange(state.homeDateFilter);
  const tx = filterForRange(state.dashboardTransactions, range.from, range.to);
  const totals = computeTotals(tx);
  const prevTotals = range.prevFrom
    ? computeTotals(filterForRange(state.dashboardTransactions, range.prevFrom, range.prevTo))
    : null;

  return `
    ${kpiGridHtml(state, totals, prevTotals)}
    <div class="homeChartsGrid">
      ${incomeExpenseChartHtml(state, tx, range)}
      ${expenseCategoryChartHtml(tx)}
    </div>
    <div class="homeTablesGrid">
      ${topClientsTableHtml(tx, state.sites)}
      ${activeProjectsTableHtml(tx, state.sites)}
    </div>
    ${recentTransactionsTableHtml(state, tx)}
    ${projectHealthSection(state)}
  `;
}

export function wireHomeScreen(shell) {
  shell.querySelector('#homeDateFilterSelect')?.addEventListener('change', (e) => {
    setState({ homeDateFilter: e.target.value });
  });
  shell.querySelector('#homeAvatarBtn')?.addEventListener('click', openAccountSheet);

  shell.querySelectorAll('[data-view-all]').forEach((btn) => {
    btn.addEventListener('click', () => openViewAllSheet(btn.dataset.viewAll, getState()));
  });
  shell.querySelectorAll('[data-client]').forEach((row) => {
    row.addEventListener('click', () => openClientDrilldownSheet(row.dataset.client, getState()));
  });
  shell.querySelectorAll('[data-site-id]').forEach((row) => {
    row.addEventListener('click', () => openSiteDrilldownSheet(row.dataset.siteId, getState()));
  });
  shell.querySelectorAll('[data-retry]').forEach((btn) => {
    btn.addEventListener('click', () => {
      loadSites();
      loadDashboardTransactions();
      loadProjectHealth();
    });
  });
}
